import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { deleteSessionItem, getSessionItem } from '@atlassian/search-dialog/session-storage-utils';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import {
	onStreamingEarlyStopCardDismissed,
	onStreamingEarlyStopCardShown,
} from '../../../ai-answer-dialog/analytics';
import { useAnalytics } from '../../../common/controller/analytics/use-analytics';
import { SEARCH_AI_ANSWER_CACHE_KEY } from '../../../constants';
import { KnowledgeCard } from '../../knowledge-cards/base-components/KnowledgeCard';
import { OnlineDebuggerButton } from '../../online-debugger/online-debugger-button';

import { messages } from './messages';

const styles = cssMap({
	root: {
		width: '100%',
	},
	onlineDebuggerButtonContainer: {
		marginLeft: 'auto',
		width: 'fit-content',
	},
});

export const StreamingEarlyStopCard: () => React.JSX.Element | null = () => {
	const [dismissed, setDismissed] = useState(false);

	const { formatMessage } = useIntl();
	const { baseQuery, commonAttributes } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();

	useEffect(() => {
		const cachedAnswer = getSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
		// Only fire analytics event once per cached answer
		if (cachedAnswer) {
			deleteSessionItem(SEARCH_AI_ANSWER_CACHE_KEY);
			fireAnalyticsEvent(onStreamingEarlyStopCardShown(commonAttributes));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const dismissCard = useCallback(() => {
		setDismissed(true);
		fireAnalyticsEvent(onStreamingEarlyStopCardDismissed(commonAttributes));
	}, [fireAnalyticsEvent, commonAttributes]);

	const showOnlineDebuggerButton = fg('kd_sain_online_debugger');

	if (dismissed) {
		return null;
	}

	return (
		<KnowledgeCard
			name="sain-streaming-early-stop-card"
			size="full"
			testId="sain-streaming-early-stop-card"
		>
			<Stack space="space.100" xcss={styles.root}>
				<Inline spread="space-between">
					<Text weight="bold">
						{formatMessage(messages.ai_answer_card_early_stop_text, {
							searchQuery: baseQuery,
						})}
					</Text>
					<IconButton
						icon={CrossIcon}
						label={formatMessage(messages.ai_answer_card_early_stop_close_button_label)}
						onClick={dismissCard}
						appearance="subtle"
						spacing="compact"
						testId="sain-early-stop-error-card-dismiss-button"
					/>
				</Inline>
				{showOnlineDebuggerButton && (
					<Box xcss={styles.onlineDebuggerButtonContainer}>
						<OnlineDebuggerButton />
					</Box>
				)}
			</Stack>
		</KnowledgeCard>
	);
};
